// Hero.js
import { useState, useEffect, useRef } from "react";

const slides = [
  { src: "/hero1.jpg", alt: "Media Production Studio" },
  { src: "/hero2.jpg", alt: "Video Recording" },
  { src: "/hero3.jpg", alt: "Broadcasting Equipment" },
  { src: "/hero4.jpg", alt: "Live Broadcasting" },
  { src: "/hero5.jpg", alt: "Community Relations" },
  { src: "/hero6.jpg", alt: "Media Team at Work" },
];

function ParticleCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let particles = [];

    function resize() {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    }

    function makeParticle() {
      return {
        x:  Math.random() * canvas.width,
        y:  Math.random() * canvas.height,
        r:  Math.random() * 2.2 + 0.6,
        dx: (Math.random() - 0.5) * 0.5,
        dy: (Math.random() - 0.5) * 0.4,
        a:  Math.random() * 0.7 + 0.3,
      };
    }

    function init() {
      resize();
      particles = Array.from({ length: 55 }, makeParticle);
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width)  p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx   = particles[i].x - particles[j].x;
          const dy   = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 90) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(201,166,118,${0.5 * (1 - dist / 90)})`;
            ctx.lineWidth   = 0.8;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(220,185,120,${p.a})`;
        ctx.fill();
      });

      animId = requestAnimationFrame(draw);
    }

    init();
    draw();

    const ro = new ResizeObserver(init);
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        zIndex: 2,
        pointerEvents: "none",
      }}
    />
  );
}

function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);

  /* Start / restart the auto-advance timer */
  function startTimer() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
  }

  useEffect(() => {
    startTimer();
    return () => clearInterval(intervalRef.current);
  }, []);

  /* Manual navigation resets the timer so it doesn't jump immediately */
  function goTo(index) {
    setCurrentSlide(index);
    startTimer();
  }

  function prev() {
    goTo((currentSlide - 1 + slides.length) % slides.length);
  }

  function next() {
    goTo((currentSlide + 1) % slides.length);
  }

  return (
    <section className="hero">

      {/* Left side — particle network */}
      <div className="hero-side">
        <ParticleCanvas />
      </div>

      {/* Centre — image slider */}
      <div className="hero-center">
        <div className="hero-slide-container">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? "active" : ""}`}
            >
              <img
                src={slide.src}
                alt={slide.alt}
                loading={index === 0 ? "eager" : "lazy"}
              />
            </div>
          ))}
        </div>

        {/* Prev / Next arrow buttons */}
        <button
          onClick={prev}
          aria-label="Previous slide"
          style={arrowStyle("left")}
        >
          ‹
        </button>
        <button
          onClick={next}
          aria-label="Next slide"
          style={arrowStyle("right")}
        >
          ›
        </button>

        {/* Dot indicators */}
        <div className="slider-indicators">
          {slides.map((_, index) => (
            <span
              key={index}
              className={`indicator ${index === currentSlide ? "active" : ""}`}
              onClick={() => goTo(index)}
              role="button"
              tabIndex={0}
              aria-label={`Go to slide ${index + 1}`}
              onKeyDown={(e) => e.key === "Enter" && goTo(index)}
            />
          ))}
        </div>
      </div>

      {/* Right side — particle network */}
      <div className="hero-side">
        <ParticleCanvas />
      </div>

    </section>
  );
}

/* Arrow button style helper */
function arrowStyle(side) {
  return {
    position: "absolute",
    top: "50%",
    [side]: "14px",
    transform: "translateY(-50%)",
    zIndex: 4,
    background: "rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.2)",
    color: "white",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    fontSize: "24px",
    lineHeight: "1",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s",
    backdropFilter: "blur(4px)",
    paddingBottom: "2px",
  };
}

export default Hero;